import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    Input,
    ListTable,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { TaxRateCoverage, TaxRateRun, TaxSeedDiff } from '@/api/types';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

const COVERAGE = '/staff/tax-rates/coverage';
const RUNS = '/staff/tax-rate-runs';
const DIFFS = '/staff/tax-rates/diffs';

const pct = (bps: number) => `${(bps / 100).toFixed(2)} %`;

/** BM-126 2.1b: one state's disputed ZIPs, the seed against the official file, widest gap first. */
const DIFF_COLUMNS: ListColumnDef<TaxSeedDiff>[] = [
    {
        key: 'zip',
        label: 'ZIP',
        cell: (r) => <span className="font-mono">{r.zip}</span>,
        locked: 'start',
    },
    { key: 'city', label: 'City', cell: (r) => r.city || '—' },
    { key: 'seed', label: 'Seed', cell: (r) => pct(r.seed_bps) },
    { key: 'check', label: 'Official file', cell: (r) => pct(r.check_bps) },
    { key: 'diff', label: 'Gap', cell: (r) => pct(r.diff_bps) },
    {
        key: 'source',
        label: 'Compared with',
        cell: (r) => (
            <span className="font-mono text-xs">{r.check_source}</span>
        ),
    },
];

/** A disputed ZIP is a flag for staff, not a correction: the seed keeps its rate until a provider quote teaches the store. */
function DisputedSheet({
    state,
    onClose,
}: {
    state: string | null;
    onClose: () => void;
}) {
    const diffs = useOne<TaxSeedDiff[]>(
        state ? `${DIFFS}?state=${state}&per_page=100` : null,
    );
    const rows = diffs.data?.data ?? [];
    return (
        <Sheet
            open={state !== null}
            onOpenChange={(o) => (o ? null : onClose())}
        >
            <SheetContent
                className="w-full sm:max-w-2xl"
                data-testid="tax-rate-diffs"
            >
                <SheetHeader>
                    <SheetTitle>Disputed ZIPs in {state}</SheetTitle>
                    <SheetDescription>
                        ZIPs where the official file differs from the seed by
                        more than 0.25 %. The seed keeps its rate; a provider
                        quote for the ZIP takes over.
                    </SheetDescription>
                </SheetHeader>
                <div className="overflow-y-auto px-4 pb-4 text-sm">
                    {diffs.isLoading ? (
                        <p className="text-muted-foreground">Loading…</p>
                    ) : rows.length === 0 ? (
                        <p className="text-muted-foreground">
                            No disputed ZIPs.
                        </p>
                    ) : (
                        <ListTable
                            columns={DIFF_COLUMNS}
                            rows={rows}
                            rowKey={(r) => r.zip}
                            stickyHeader={false}
                        />
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

/** Coverage per state (BM-84: the kit's table, rule 0.1). */
const coverageColumns = (
    onDisputed: (state: string) => void,
): ListColumnDef<TaxRateCoverage>[] => [
    {
        key: 'region',
        label: 'State',
        cell: (r) => <span className="font-mono">{r.region}</span>,
        locked: 'start',
    },
    { key: 'seed_zips', label: 'Seed ZIPs', cell: (r) => r.seed_zips },
    { key: 'seed', label: 'Seed', cell: (r) => r.seed_version ?? '—' },
    { key: 'zip4', label: 'ZIP+4 ranges', cell: (r) => r.zip4_ranges ?? 0 },
    { key: 'learned', label: 'Learned ZIPs', cell: (r) => r.learned_zips },
    {
        key: 'oldest',
        label: 'Oldest quote',
        kind: 'datetime',
        cell: (r) => <DateTime value={r.oldest_quoted_at} empty="—" />,
        noTruncate: true,
    },
    { key: 'taxability', label: 'Taxability', cell: (r) => r.taxability_rows },
    {
        key: 'diffs',
        label: 'Disputed',
        cell: (r) =>
            r.diffs ? (
                <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => onDisputed(r.region)}
                    data-testid={`tax-rate-disputed-${r.region}`}
                >
                    {r.diffs}
                </Button>
            ) : (
                0
            ),
    },
];

/**
 * BM-67 2.7, BM-126: the ZIP rate store behind the fallback quote. Coverage per state (the seeded ZIPs and ZIP+4
 * ranges, the ZIPs the provider taught this tenant, the taxability rows, the ZIPs an official file disputed, listed
 * in a sheet), the warm-up runs with their stop, and the button that queues a run for a state through the tenant's
 * provider (payments.tax.manage).
 */
export function TaxRateStoreCard() {
    const can = useAuthStore((s) => s.can);
    const [state, setState] = useState('');
    const [error, setError] = useState<string | null>(null);
    const coverage = useOne<TaxRateCoverage[]>(COVERAGE);
    const runs = useOne<TaxRateRun[]>(`${RUNS}?per_page=10`);
    const start = useWrite(
        async (states: string[]) =>
            (await api().post<TaxRateRun>(RUNS, { states })).data,
        [RUNS, COVERAGE],
    );
    const stop = useWrite(
        async (id: string) =>
            (
                await api().post<TaxRateRun>(`${RUNS}/${id}/stop`, undefined, {
                    idempotencyKey: crypto.randomUUID(),
                })
            ).data,
        [RUNS, COVERAGE],
    );
    const [disputed, setDisputed] = useState<string | null>(null);
    const rows = coverage.data?.data ?? [];
    const latest = runs.data?.data ?? [];
    // the TaxLocus snapshot the taxability rows came from (the newest of the states')
    const snapshot = rows
        .map((r) => r.taxability_version ?? '')
        .reduce((a, b) => (b > a ? b : a), '');
    const valid = /^[A-Za-z]{2}$/.test(state.trim());
    return (
        <Card className="mb-4" data-testid="tax-rate-store">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm font-medium">
                    Rate store
                </CardTitle>
                {can(P.taxManage) ? (
                    <form
                        method="post"
                        className="flex items-center gap-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (!valid) return;
                            setError(null);
                            start.mutate([state.trim().toUpperCase()], {
                                onSuccess: () => setState(''),
                                onError: (err) =>
                                    setError(apiErrorMessage(err)),
                            });
                        }}
                    >
                        <Input
                            aria-label="State to warm up"
                            placeholder="CA"
                            className="w-20 font-mono text-xs uppercase"
                            maxLength={2}
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                        />
                        <Button
                            type="submit"
                            size="sm"
                            variant="outline"
                            disabled={!valid || start.isPending}
                            data-testid="tax-rate-run-state"
                        >
                            Run state
                        </Button>
                    </form>
                ) : null}
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground text-xs">
                    The fallback when the provider is down: the rate it taught
                    us for the ZIP and code; where the seed fallback flag is on,
                    the seeded rate (the ZIP+4 range, else a ZIP with one rate)
                    when it is newer, with the state&apos;s taxability of the
                    code known. Anything else is refused, never guessed. A run
                    quotes a reference line per ZIP through the provider.
                </p>
                {error ? (
                    <p role="alert" className="text-destructive text-sm">
                        {error}
                    </p>
                ) : null}
                {coverage.isLoading ? (
                    <p className="text-muted-foreground">Loading…</p>
                ) : rows.length === 0 ? (
                    <p
                        className="text-muted-foreground"
                        data-testid="tax-rate-store-empty"
                    >
                        No seed imported yet (the deploy imports
                        database/data/tax) and nothing learned.
                    </p>
                ) : (
                    // BM-84 (rule 0.1): coverage per state through the kit's table
                    <ListTable
                        columns={coverageColumns(setDisputed)}
                        rows={rows}
                        rowKey={(r) => r.region}
                        stickyHeader={false}
                    />
                )}
                {latest.length > 0 ? (
                    <div>
                        <p className="text-muted-foreground mb-1 text-xs">
                            Latest runs
                        </p>
                        <ul
                            className="space-y-1 text-xs"
                            data-testid="tax-rate-runs"
                        >
                            {latest.map((r) => (
                                <li
                                    key={r.id}
                                    className="flex flex-wrap items-center gap-2"
                                >
                                    <StatusBadge
                                        status={r.state}
                                        domain={ADMIN_STATUSES.taxRateRun}
                                    />
                                    <span className="font-mono">
                                        {r.states.join(', ')}
                                    </span>
                                    <span>
                                        {r.zips_done}/{r.zips_total} ZIPs
                                        {r.errors ? `, ${r.errors} errors` : ''}
                                    </span>
                                    <span className="text-muted-foreground">
                                        <DateTime
                                            value={r.created_at}
                                            empty="—"
                                        />
                                    </span>
                                    {r.error ? (
                                        <span className="text-destructive">
                                            {r.error}
                                        </span>
                                    ) : null}
                                    {/* BM-126 decision 13: the staff stop that replaced the kill switch */}
                                    {can(P.taxManage) &&
                                    (r.state === 'queued' ||
                                        r.state === 'running') ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={stop.isPending}
                                            onClick={() => {
                                                setError(null);
                                                stop.mutate(r.id, {
                                                    onError: (err) =>
                                                        setError(
                                                            apiErrorMessage(
                                                                err,
                                                            ),
                                                        ),
                                                });
                                            }}
                                            data-testid={`tax-rate-run-stop-${r.id}`}
                                        >
                                            Stop
                                        </Button>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
                <p
                    className="text-muted-foreground text-xs"
                    data-testid="tax-rate-attribution"
                >
                    Sales-tax data by TaxLocus
                    {snapshot ? (
                        <>
                            , snapshot{' '}
                            <DateTime value={snapshot} format="date" />
                        </>
                    ) : null}{' '}
                    —{' '}
                    <a
                        href="https://taxlocus.com"
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                    >
                        taxlocus.com
                    </a>
                    . ZIP rates from OpenSalesTax and the states&apos; official
                    files.
                </p>
                <DisputedSheet
                    state={disputed}
                    onClose={() => setDisputed(null)}
                />
            </CardContent>
        </Card>
    );
}
